// src/components/HomeroomUpload.js
import React, { useState } from 'react';
import axios from 'axios';

// Function to get the CSRF token from the browser's cookies
function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function HomeroomUpload() {
    const [file, setFile] = useState(null);
    const [columnMapping, setColumnMapping] = useState({
        student_email: '',
        teacher_email: '',
        homeroom_name: ''
    });
    const [clearExisting, setClearExisting] = useState(false);
    const [useHomeroomLookup, setUseHomeroomLookup] = useState(true);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [showColumnMapping, setShowColumnMapping] = useState(false);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setUploadStatus(null);
        setUploadResults(null);
        setAvailableColumns([]);
        setShowColumnMapping(false);
    };

    // Preview file columns and detect data patterns
    const previewColumns = async () => {
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('preview_only', 'true');

        try {
            const response = await axios.post('/students/api/upload-homeroom-teachers/', formData, {
                headers: { 'X-CSRFToken': getCookie('csrftoken') }
            });
            if (response.data.available_columns) {
                setAvailableColumns(response.data.available_columns);
                setShowColumnMapping(true);
                
                // Auto-detect Infinite Campus format
                const columns = response.data.available_columns;
                if (columns.includes('pcontact_email') && columns.includes('courseSection_TeacherEmail')) {
                    setColumnMapping({
                        student_email: 'pcontact_email',
                        teacher_email: 'courseSection_TeacherEmail',
                        homeroom_name: 'courseSection_courseName'
                    });
                }
            }
        } catch (error) {
            console.error('Error previewing file:', error);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!file) {
            alert('Please select a file first.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('clear_existing', clearExisting);
        formData.append('use_homeroom_lookup', useHomeroomLookup);
        
        // Add column mapping if provided
        if (columnMapping.student_email || columnMapping.teacher_email || columnMapping.homeroom_name) {
            formData.append('column_mapping', JSON.stringify(columnMapping));
        }

        const csrfToken = getCookie('csrftoken');
        setUploadStatus('uploading');

        axios.post('/students/api/upload-homeroom-teachers/', formData, {
            headers: {
                'X-CSRFToken': csrfToken
            }
        })
        .then(response => {
            setUploadStatus('success');
            setUploadResults(response.data);
            
            // Show success message with details
            const { message, errors, method_used } = response.data;
            let alertMessage = `✅ ${message}\n\nMethod used: ${method_used === 'teacher_email' ? 'Teacher Email Assignment' : 'Homeroom Name Lookup'}`;
            
            if (response.data.columns_used) {
                alertMessage += `\n\nColumns used:`;
                Object.entries(response.data.columns_used).forEach(([key, value]) => {
                    if (value) alertMessage += `\n• ${key}: ${value}`;
                });
            }
            
            alert(alertMessage);
            
            // Handle errors separately for better UX
            if (errors && errors.length > 0) {
                setUploadResults(prev => ({ ...prev, hasErrors: true }));
            }
        })
        .catch(error => {
            setUploadStatus('error');
            console.error('Error uploading homeroom teachers:', error);
            const errorMessage = error.response?.data?.message || 'Upload failed';
            alert(`❌ Error: ${errorMessage}`);
            
            // Show available columns if provided in error response
            if (error.response?.data?.available_columns) {
                setAvailableColumns(error.response.data.available_columns);
                setShowColumnMapping(true);
            }
        });
    };

    const showDetailedResults = () => {
        if (!uploadResults) return;
        
        const { errors, method_used } = uploadResults;
        let details = `Upload completed using: ${method_used === 'teacher_email' ? 'Teacher Email Assignment' : 'Homeroom Name Lookup'}\n\n`;
        
        if (errors && errors.length > 0) {
            details += `Errors encountered (${errors.length}):\n`;
            errors.slice(0, 20).forEach((error, index) => {
                details += `${index + 1}. ${error}\n`;
            });
            if (errors.length > 20) {
                details += `... and ${errors.length - 20} more errors.`;
            }
        } else {
            details += 'No errors encountered! All assignments processed successfully.';
        }
        
        alert(details);
    };

    return (
        <div style={{ padding: '20px', maxWidth: '700px' }}>
            <h2>Upload Homeroom Teachers</h2>
            
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                <h4 style={{ color: '#495057', marginBottom: '10px' }}>📋 Infinite Campus Integration</h4>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    <p><strong>Supported formats:</strong> Excel (.xlsx, .xls) and CSV files</p>
                    <p><strong>Expected columns:</strong></p>
                    <ul style={{ marginBottom: '10px' }}>
                        <li><code>pcontact_email</code> - Student email addresses</li>
                        <li><code>courseSection_TeacherEmail</code> - Teacher email addresses (when assigned)</li>
                        <li><code>courseSection_courseName</code> - Homeroom names (for unassigned students)</li>
                    </ul>
                    <div style={{ padding: '10px', backgroundColor: '#d1ecf1', borderRadius: '3px', border: '1px solid #bee5eb' }}>
                        <strong>💡 Mixed Assignment Support:</strong> This system handles both assigned students (with teacher emails) 
                        and unassigned students (using homeroom name lookup) in the same upload.
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Select File:
                    </label>
                    <input 
                        type="file" 
                        onChange={handleFileChange} 
                        accept=".csv,.xlsx,.xls" 
                        required 
                        style={{ padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                    />
                </div>

                {file && (
                    <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                        <button 
                            type="button" 
                            onClick={previewColumns}
                            style={{ 
                                marginRight: '10px', 
                                padding: '6px 12px', 
                                backgroundColor: '#6c757d', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            🔍 Preview Columns
                        </button>
                        <span style={{ fontSize: '14px', color: '#495057' }}>
                            📁 Selected: <strong>{file.name}</strong>
                        </span>
                    </div>
                )}

                {showColumnMapping && availableColumns.length > 0 && (
                    <div style={{ marginBottom: '15px', padding: '15px', border: '1px solid #ced4da', borderRadius: '5px', backgroundColor: '#f8f9fa' }}>
                        <h4 style={{ marginBottom: '10px', color: '#495057' }}>🎯 Column Mapping</h4>
                        <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                            <strong>Available columns:</strong> {availableColumns.join(', ')}
                        </p>
                        
                        <div style={{ display: 'grid', gap: '10px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                                    Student Email Column:
                                </label>
                                <select 
                                    value={columnMapping.student_email}
                                    onChange={(e) => setColumnMapping({...columnMapping, student_email: e.target.value})}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                >
                                    <option value="">Auto-detect</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                                    Teacher Email Column:
                                </label>
                                <select 
                                    value={columnMapping.teacher_email}
                                    onChange={(e) => setColumnMapping({...columnMapping, teacher_email: e.target.value})}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                >
                                    <option value="">Auto-detect</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                                    Homeroom Name Column:
                                </label>
                                <select 
                                    value={columnMapping.homeroom_name}
                                    onChange={(e) => setColumnMapping({...columnMapping, homeroom_name: e.target.value})}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                >
                                    <option value="">Auto-detect</option>
                                    {availableColumns.map(col => (
                                        <option key={col} value={col}>{col}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: '15px', padding: '15px', border: '1px solid #ced4da', borderRadius: '5px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#495057' }}>⚙️ Upload Options</h4>
                    
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <input 
                                type="checkbox" 
                                checked={useHomeroomLookup}
                                onChange={(e) => setUseHomeroomLookup(e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            <strong>Enable homeroom name lookup</strong>
                            <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                                (for students without teacher emails)
                            </span>
                        </label>
                    </div>
                    
                    <div>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                            <input 
                                type="checkbox" 
                                checked={clearExisting}
                                onChange={(e) => setClearExisting(e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            <strong>Clear existing homeroom assignments</strong>
                            <span style={{ marginLeft: '8px', color: '#dc3545' }}>
                                (⚠️ removes all current assignments first)
                            </span>
                        </label>
                    </div>
                </div>

                <button 
                    type="submit" 
                    disabled={uploadStatus === 'uploading'}
                    style={{ 
                        padding: '12px 24px', 
                        backgroundColor: uploadStatus === 'uploading' ? '#6c757d' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        width: '100%'
                    }}
                >
                    {uploadStatus === 'uploading' ? '⏳ Uploading...' : '📤 Upload Homeroom Assignments'}
                </button>
            </form>

            {uploadStatus === 'success' && uploadResults && (
                <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#d4edda', 
                    color: '#155724', 
                    borderRadius: '5px',
                    border: '1px solid #c3e6cb'
                }}>
                    <h4 style={{ marginBottom: '10px' }}>✅ Upload Completed Successfully!</h4>
                    <p style={{ marginBottom: '10px' }}>
                        <strong>Method used:</strong> {uploadResults.method_used === 'teacher_email' ? 'Teacher Email Assignment' : 'Homeroom Name Lookup'}
                    </p>
                    {uploadResults.hasErrors && (
                        <button 
                            onClick={showDetailedResults}
                            style={{ 
                                padding: '6px 12px', 
                                backgroundColor: '#ffc107', 
                                color: '#212529', 
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            ⚠️ View Error Details
                        </button>
                    )}
                </div>
            )}

            {uploadStatus === 'error' && (
                <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#f8d7da', 
                    color: '#721c24', 
                    borderRadius: '5px',
                    border: '1px solid #f5c6cb'
                }}>
                    <h4>❌ Upload Failed</h4>
                    <p>Please check your file format and try again. Use the "Preview Columns" feature to verify your data structure.</p>
                </div>
            )}
        </div>
    );
}

export default HomeroomUpload;
